import { BUILD_VERSION } from "@/apolloClient";
import { CLIENT_VERSION_SUBSCRIPTION } from "@/graphql/clientVersion";
import { useSubscription } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";

interface VersionUpdateData {
    clientVersionUpdated: {
        version: string;
        force: boolean;
        message: string | null;
    };
}

interface UseClientVersionOptions {
    /**
     * Whether to auto-refresh when a new version is detected.
     * Default: true - the page will automatically refresh.
     * Set to false to show a prompt instead.
     */
    autoRefresh?: boolean;
    /**
     * Delay in milliseconds before auto-refreshing.
     * Gives time for any pending operations to complete.
     * Default: 2000ms (2 seconds)
     */
    refreshDelay?: number;
}

interface UseClientVersionResult {
    /** The current build version of this client */
    currentVersion: string;
    /** Whether a newer version is available */
    isOutdated: boolean;
    /** The new version available (if outdated) */
    newVersion: string | null;
    /** Optional message from the server about the update */
    updateMessage: string | null;
    /** Whether auto-refresh is pending */
    isRefreshing: boolean;
    /** Trigger a page refresh to load the new version */
    refresh: () => void;
}

/**
 * Hook to track client version and auto-refresh when updates are available.
 *
 * By default, this will automatically refresh the page when a new version
 * is detected to ensure users always have the latest code.
 *
 * @param options Configuration options
 * @returns Version status and controls
 */
export function useClientVersion(
    options: UseClientVersionOptions = {},
): UseClientVersionResult {
    const { autoRefresh = true, refreshDelay = 2000 } = options;

    const [isOutdated, setIsOutdated] = useState(false);
    const [newVersion, setNewVersion] = useState<string | null>(null);
    const [updateMessage, setUpdateMessage] = useState<string | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const refresh = useCallback(() => {
        setIsRefreshing(true);
        // Force a hard refresh to bypass cache
        window.location.reload();
    }, []);

    // Subscribe to version updates via WebSocket
    useSubscription<VersionUpdateData>(CLIENT_VERSION_SUBSCRIPTION, {
        onData: ({ data }) => {
            const versionData = data.data?.clientVersionUpdated;
            if (versionData && versionData.version !== BUILD_VERSION) {
                console.info(
                    `[ClientVersion] New version available: ${versionData.version} (current: ${BUILD_VERSION})`,
                );
                setIsOutdated(true);
                setNewVersion(versionData.version);
                setUpdateMessage(versionData.message);

                // Auto-refresh after a short delay
                if (autoRefresh) {
                    console.info(
                        `[ClientVersion] Auto-refreshing in ${refreshDelay}ms...`,
                    );
                    setIsRefreshing(true);
                    setTimeout(() => {
                        window.location.reload();
                    }, refreshDelay);
                }
            }
        },
        onError: (error) => {
            console.error("Client version subscription error:", error);
        },
    });

    // Also check response headers for outdated status
    useEffect(() => {
        // Listen for custom event that can be dispatched by an Apollo afterware
        const handleOutdatedResponse = (
            event: CustomEvent<{ version: string }>,
        ) => {
            if (event.detail.version !== BUILD_VERSION) {
                console.info(
                    `[ClientVersion] Outdated version detected via API response`,
                );
                setIsOutdated(true);
                setNewVersion(event.detail.version);

                // Auto-refresh after a short delay
                if (autoRefresh && !isRefreshing) {
                    console.info(
                        `[ClientVersion] Auto-refreshing in ${refreshDelay}ms...`,
                    );
                    setIsRefreshing(true);
                    setTimeout(() => {
                        window.location.reload();
                    }, refreshDelay);
                }
            }
        };

        window.addEventListener(
            "client-outdated",
            handleOutdatedResponse as EventListener,
        );

        return () => {
            window.removeEventListener(
                "client-outdated",
                handleOutdatedResponse as EventListener,
            );
        };
    }, [autoRefresh, refreshDelay, isRefreshing]);

    return {
        currentVersion: BUILD_VERSION,
        isOutdated,
        newVersion,
        updateMessage,
        isRefreshing,
        refresh,
    };
}
